import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { Viewer } from "../shared/contracts";
import { ApiError, apiRequest } from "./api";

type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "error";

type AuthContextValue = {
  status: AuthStatus;
  viewer: Viewer | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [viewer, setViewer] = useState<Viewer | null>(null);

  const refresh = useCallback(async () => {
    setStatus("loading");
    try {
      const loaded = await apiRequest<Viewer>("/api/v1/me");
      setViewer(loaded);
      setStatus("authenticated");
    } catch (error) {
      const isUnauthenticated = error instanceof ApiError && error.status === 401;
      setViewer(null);
      setStatus(isUnauthenticated ? "unauthenticated" : "error");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ status?: number }>).detail;
      if (detail?.status === 401) {
        setViewer(null);
        setStatus("unauthenticated");
      }
    };
    window.addEventListener("creator-radar:api-error", listener);
    return () => window.removeEventListener("creator-radar:api-error", listener);
  }, []);

  const signOut = useCallback(async () => {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "same-origin",
    });
    setViewer(null);
    setStatus("unauthenticated");
  }, []);

  const value = useMemo(() => ({ status, viewer, refresh, signOut }), [status, viewer, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider.");
  return value;
}
